import {ExpressionElement, type Expression} from '@packages/systems/wfdl-ast';
import {namesEqual, qualify} from '@packages/systems/wfdl-Qualified';
import {
  isFormButtonElement,
  isLineBreakElement,
  isTextHolder,
} from '@packages/utilities/elementTypeUtils';
import {Expressions, matchExpression} from '@packages/systems/wfdl';
import {concatTo, emptyArray} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';
import {getInstanceChildrenPropId} from './getInstanceChildrenPropId';

const findNonTextHolders = (expr: Expression) => {
  return Expressions.everything<ExpressionElement[]>(concatTo)(
    matchExpression({
      Element: (el) => {
        const isNonTextHolder =
          !isTextHolder(el) &&
          !namesEqual(el.type, qualify('Basic', 'String')) &&
          !isLineBreakElement(el);
        return isNonTextHolder
          ? Expressions.resume([el])
          : Expressions.resume(emptyArray);
      },
      _: () => Expressions.resume(emptyArray),
    })
  )(expr);
};

/**
 * Determines if an element only contains text content or children
 * with text content. This is used in AI suggestions to allow replacement
 * of elements with any text content with suggestions. It is intended to
 * ignore children that are formatting text content (e.g. spans, line breaks, etc.).
 *
 * @param selectedElement - The element to check
 * @returns true if the element only contains text, false otherwise
 */
const getOnlyContainsTextImpl = weakMemo(
  (selectedElement: ExpressionElement) => {
    // Form button elements are only allowed to contain text
    const isFormButton = isFormButtonElement(selectedElement);
    if (isFormButton) {
      return true;
    }

    // If the element is a component instance with a prop, we assume it will be
    // a text element because we only allow text children for allowed elements
    const propId = getInstanceChildrenPropId(selectedElement);
    if (propId) {
      return true;
    }

    // Check only descendants, not the element itself
    // Get the element's data expression and search that
    const elementData = Expressions.getElementData(selectedElement);
    const nonTextHolders = findNonTextHolders(elementData);

    return nonTextHolders.length === 0;
  }
);

export function getOnlyContainsText(
  selectedElement: ExpressionElement | null
): boolean {
  if (!selectedElement) {
    return false;
  }

  return getOnlyContainsTextImpl(selectedElement);
}
